import { StatisticType } from '../types/dashboard.type';
import { LIST_STATISTIC_TYPES, isStatAvailable } from './dashboardStats.catalog';

/**
 * A KPI can carry a threshold only when the dashboard reports it as a single,
 * real number: breakdowns / map payloads are lists, and stats the API still
 * hard-codes to 0 would always "meet" or "miss" a target meaninglessly.
 * Existing limits on such keys are kept but flagged in the dialog.
 */
export function isTrackableKpi(key: string): boolean {
  const normalized = key.toLowerCase();
  if (LIST_STATISTIC_TYPES.has(normalized as StatisticType)) return false;
  if (normalized === 'outgoing_waste_breakdown') return false;
  return isStatAvailable(normalized) && (Object.values(StatisticType) as string[]).includes(normalized);
}

/** Every trackable statistic (used when no org-specific list is given). */
export const TRACKABLE_KPI_OPTIONS: StatisticType[] = Object.values(StatisticType).filter(isTrackableKpi);

/** A KPI value usable as a target reading, or null when the period has none. */
export function kpiTargetValue(source: object | null | undefined, key: string): number | null {
  const raw = source ? (source as Record<string, unknown>)[key.toLowerCase()] : undefined;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

export type KpiOperator = '>' | '<' | '=' | '>=' | '<=';

/**
 * Whether the limit's condition holds for `value` — a KPI limit reads "alert
 * when <KPI> <operator> <value>", so true means the alert fires. Null when the
 * period has no reading.
 */
export function kpiAlertFires(value: number | null, operator: KpiOperator, target: number): boolean | null {
  if (value === null) return null;
  switch (operator) {
    case '>':
      return value > target;
    case '>=':
      return value >= target;
    case '<':
      return value < target;
    case '<=':
      return value <= target;
    case '=':
      return Math.abs(value - target) < 0.01;
    default:
      return null;
  }
}
